"use strict";

import "whatwg-fetch";

import { optionsGen } from "@/helpers/api";
import { toTitleCase } from "@/helpers/converters";
import { API_URL } from "@/config";
import { toESQuery, toESGroupByQuery, toESDateHistogramQuery, toESDateHistogramGroupByQuery, toESDoubleGroupByQuery } from "@/helpers/query";
import { omit } from "lodash";

const AUTH = false;
export const REEXAM_URL = `${API_URL}/prosecution/`;
export const REEXAM_SEARCH_URL = `${API_URL}/patents/prosecution/_search`;

export const scopeQuery = query => {
  // scoped to ex-parte reexam apps after 2001 only
  // no annotation for pre-2001 apps due to CMS docs unavailable before then
  let queryNew = { ...query, series_code: 90 };
  if (queryNew.filing_date) {
    const dates = queryNew.filing_date.split("--");
    if (dates[0] < "2001-01-01") {
      queryNew.filing_date = `2001-01-01--${dates[1] || ""}`;
    }
  } else {
    // Otherwise if it is missing the filing date, default to 2001.
    queryNew.filing_date = "2001-01-01--";
  }
  return queryNew;
};

// The prosecution index has no nested fields (`documents`/`transactions` are plain object
// arrays), so disable toESQuery's nested wrapping. Object sub-fields like `documents.code`
// then match through the normal case-insensitive wildcard path. Applies to the search query
// and every aggregation below — otherwise a `documents.code` filter (the Has Document filter)
// triggers a `[nested] ... is not of nested type` shard exception.
const NO_NESTED = { nestedPrefixes: [] };

export function esqCases(query, highlight = false) {
  return toESQuery(scopeQuery(query), [], 20, highlight, NO_NESTED);
}

// The shared aggregation builders (toESGroupByQuery, etc.) default to nested wrapping, which
// this index rejects. Rebuild just the `query` clause without it, keeping the builder's aggs.
// `queries` must already be scopeQuery'd (same object the builder received).
const flatQuery = (esq, queries) => ({ ...esq, query: toESQuery(queries, [], 0, false, NO_NESTED).query });

export function getCases(query) {
  return fetch(REEXAM_SEARCH_URL, optionsGen("POST", esqCases(query), AUTH)).then(resp => resp.json());
}

export function getDistinctValues(field, query) {
  const queries = scopeQuery(query);
  return fetch(REEXAM_SEARCH_URL, optionsGen("POST", flatQuery(toESGroupByQuery(field, queries, 50), queries), AUTH))
    .then(resp => resp.json())
    .then(json => {
      return json.aggregations[field].buckets.map(e => toTitleCase(e.key));
    });
}

// Distinct values of an object sub-field code (e.g. `documents.code`, `transactions.code`)
// for a filter autocomplete. Uses a plain terms aggregation (these are object sub-fields,
// not nested) with an uppercase `include` regex to narrow to the typed text. Returns raw
// uppercase codes.
const escapeRegexp = s => s.replace(/[.?+*|(){}[\]"\\&~<>@#]/g, "\\$&");

export function getCodeOptions(field, searchVal) {
  const scoped = scopeQuery({});
  const esq = flatQuery(toESGroupByQuery(field, scoped, 50), scoped);
  const search = (searchVal || "").toUpperCase();
  if (search) esq.aggs[field].terms.include = `.*${escapeRegexp(search)}.*`;
  return fetch(REEXAM_SEARCH_URL, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => json.aggregations[field].buckets.map(b => b.key));
}

export function getCaseDetail(casenum) {
  return fetch(`${REEXAM_URL}${casenum}`, optionsGen("GET", false, AUTH)).then(resp => resp.json());
}

export function countCasesByField(query, field, size = 20) {
  const queries = scopeQuery(query);
  return fetch(REEXAM_SEARCH_URL, optionsGen("POST", flatQuery(toESGroupByQuery(field, queries, size), queries), AUTH))
    .then(resp => resp.json())
    .then(json => {
      let total = json.aggregations[field].sum_other_doc_count;
      json.aggregations[field].buckets.forEach(b => {
        total += b.doc_count;
      });
      return json.aggregations[field].buckets.map(b => ({
        ...b,
        doc_pct: b.doc_count / total
      }));
    });
}

export function countCasesByDate(query, dateField, interval = "year") {
  const queries = scopeQuery(query);
  return fetch(REEXAM_SEARCH_URL, optionsGen("POST", flatQuery(toESDateHistogramQuery(dateField, interval, queries), queries), AUTH))
    .then(resp => resp.json())
    .then(json => {
      return json.aggregations[dateField].buckets;
    });
}

export function countCasesByDateAndField(query, dateField, field2, interval = "year") {
  const queries = scopeQuery(query);
  let q = flatQuery(toESDateHistogramGroupByQuery(dateField, field2, interval, queries), queries);
  return fetch(REEXAM_SEARCH_URL, optionsGen("POST", q, AUTH))
    .then(resp => resp.json())
    .then(json =>
      json.aggregations[dateField].buckets.map(b => ({
        key: b.key,
        key_as_string: b.key_as_string,
        doc_count: b.doc_count,
        value: b[field2].buckets.map(bb => ({
          key: bb.key,
          doc_count: bb.doc_count
        }))
      }))
    );
}

export function getCasesByFieldCancelRate(queries) {
  const field = queries.field;
  const query = omit(queries, ["field"]);
  const terminatedQuery = scopeQuery({ ...query, exparte_status: "Terminated" });
  return fetch(REEXAM_SEARCH_URL, optionsGen("POST", flatQuery(toESDoubleGroupByQuery(field, "is_claim_cancelled", terminatedQuery, 50), terminatedQuery), AUTH))
    .then(resp => resp.json())
    .then(json => {
      const res = {};
      json.aggregations[field].buckets.forEach(b => {
        const name = toTitleCase(b.key);
        let cancelled = 0;
        let notCancelled = 0;
        b.is_claim_cancelled.buckets.forEach(bib => {
          if (bib.key === 1) cancelled = bib.doc_count;
          else notCancelled = bib.doc_count;
        });
        res[name] = { "At Least One Claim Cancelled": cancelled, "No Claim Cancelled": notCancelled };
      });
      return res;
    });
}

export function getCasesByField(query, field, labelMap, mergeThreshold = 0.01) {
  const queries = scopeQuery(query);
  return fetch(REEXAM_SEARCH_URL, optionsGen("POST", flatQuery(toESGroupByQuery(field, queries, 50), queries)))
    .then(resp => resp.json())
    .then(json => {
      const total = json.hits.total.value;
      let otherCount = json.aggregations[field].sum_other_doc_count || 0;
      let res = {};
      let keyCountPairs = json.aggregations[field].buckets;
      keyCountPairs.forEach(e => {
        const key = toTitleCase(`${e.key}`);
        const label = labelMap ? labelMap[key] || key : key;
        // put fractions less than `mergeThreshold` into `Other`
        if (e.doc_count / total >= mergeThreshold) {
          res[label] = e.doc_count;
        } else {
          otherCount += e.doc_count;
        }
      });
      if (otherCount > 0) {
        res.Other = otherCount;
      }
      return res;
    });
}
